// eslint-disable-next-line jira/import-whitelist
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { getIssueKey } from '@jira/router-resources__utils/src/getIssueKey.tsx';
import { RESOURCE_TYPE_PROJECT_CONTEXT } from '@jira/router-resources__project-context/src/resourceTypeProjectContext.constants.tsx';
import { consumeDependency } from '@jira/router-resources__utils/src/consumeDependency.tsx';
import type { ProjectContext } from '@jira/router-resources__project-context/src/ProjectContext.types.tsx';
import {
	firstProjectResource,
	requiredFrontendProperties,
	requiredProjectProperties,
} from '@jira/router-resources__service-desk-project-getting-started/src/firstProjectResource.tsx';
import { safeCloneResource } from '@jira/router-resources__utils/src/resourceWithCondition.tsx';
import { staticProjectSidebarConnectAddonsNavigationResource } from '@jira/navigation-apps__resources/src/controllers/sidebar/static-project-sidebar-connect-addons/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	createResource,
	useResource,
	type ResourceStoreContext,
	type RouterContext,
	type RouterDataContext,
	type RouteResource,
	type UseResourceHookResponse,
} from '@atlassian/react-resource-router';
import { fetchJson } from '@atlassian/jira-fetch/src/index.tsx';

type KbArticle = {
	portalId: number;
	id: string;
	shareable: boolean;
	spaceKey: string | null;
	title: string;
	unescapedTitle?: string;
	shareUrl?: URL;
	sourceType?: string;
	visibility?: string;
};

type ArticleResponse = {
	results: KbArticle[];
};

type RequestPanelMetadataResponse = {
	channel?: string;
	requestUrl?: string;
	csatComment?: string;
	errors?: unknown[];
	message?: string;
};

const wrapResourceWithServiceDeskLicenseCheck = (
	resource: RouteResource<unknown>,
): RouteResource<unknown> =>
	safeCloneResource(resource, {
		getData: async (
			routerDataContext: RouterDataContext,
			resourceStoreContext: ResourceStoreContext,
		) => {
			const { tenantContext } = resourceStoreContext;

			if (tenantContext.appEditions.serviceDesk === 'UNLICENSED') {
				return null;
			}

			try {
				return await resource.getData(routerDataContext, resourceStoreContext);
			} catch (error: unknown) {
				if (
					error instanceof Error &&
					'statusCode' in error &&
					(error.statusCode === 401 || error.statusCode === 403 || error.statusCode === 404)
				) {
					return null;
				}
				throw error;
			}
		},
	});

const jsmIssueViewSlaPanelResource = createResource({
	type: 'JSM_ISSUE_VIEW_SLA_PANEL_RESOURCE',
	getKey: ({ match, query }: RouterContext) => {
		const issueKey = getIssueKey(match);

		if (!issueKey) {
			if (query && query.selectedIssue) {
				return query.selectedIssue || '';
			}
		}

		return issueKey || '';
	},
	getData: async ({ match, query }: RouterDataContext, { tenantContext }: ResourceStoreContext) => {
		if (tenantContext.appEditions.serviceDesk === 'UNLICENSED') {
			return null;
		}
		let issueKey = getIssueKey(match);

		if (!issueKey) {
			if (query && query.selectedIssue) {
				issueKey = query.selectedIssue;
			}
		}

		if (!issueKey) {
			return null;
		}

		try {
			const url = `/rest/servicedesk/1/servicedesk/sla/issue/${issueKey}?operation=jsmIssueViewSlaPanelResource`;

			const response = await fetchJson(url, { method: 'GET' });
			if (!response.errors) {
				return response;
			}
		} catch (error: unknown) {
			fireErrorAnalytics({
				error: error instanceof Error ? error : new Error(String(error)),
				sendToPrivacyUnsafeSplunk: true,
				meta: {
					id: 'jsmIssueViewSlaPanelResource',
					packageName: 'jiraRouterResourcesJsmIssueView',
					teamName: 'jsd-shield',
				},
			});
		}

		return null;
	},
});

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const getJsmIssueViewSlaPanelResource = (): RouteResource<any> =>
	jsmIssueViewSlaPanelResource;

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const useJsmIssueViewSlaPanelResource = (): UseResourceHookResponse<any> => {
	return useResource(jsmIssueViewSlaPanelResource);
};

// eslint-disable-next-line jira/router/no-daisy-chaining-resources
const jsmIssueViewRelatedArticlesResource = createResource({
	type: 'JSM_ISSUE_VIEW_RELATED_ARTICLES_RESOURCE',
	getKey: ({ match, query }: RouterContext) => {
		const issueKey = getIssueKey(match);

		if (!issueKey) {
			if (query && query.selectedIssue) {
				return query.selectedIssue || '';
			}
		}

		return issueKey || '';
	},
	depends: [RESOURCE_TYPE_PROJECT_CONTEXT],
	getData: async (
		{ match, query, dependencies }: RouterDataContext,
		{ tenantContext }: ResourceStoreContext,
	) => {
		if (tenantContext.appEditions.serviceDesk === 'UNLICENSED') {
			return null;
		}

		let issueKey = getIssueKey(match);

		if (!issueKey) {
			if (query && query.selectedIssue) {
				issueKey = query.selectedIssue;
			}
		}

		if (!issueKey) {
			return null;
		}

		const consumedDependency = await consumeDependency<ProjectContext>(
			dependencies,
			RESOURCE_TYPE_PROJECT_CONTEXT,
		);

		if (consumedDependency) {
			const { projectType } = consumedDependency;
			if (projectType !== 'service_desk') {
				return null;
			}
		}

		if (__SERVER__) {
			const serverUrl = `/api/issue/${issueKey}/articles?operation=jsmIssueViewRelatedArticlesResourceconst `;

			return fetchJson<ArticleResponse>(serverUrl, {
				method: 'GET',
				headers: { 'x-ssr-service-name': 'jsd-apollo', 'atl-cloudid': tenantContext.cloudId },
			});
		}

		const clientUrl = `/gateway/api/jsd-apollo-stargate/sharded/workspace/${tenantContext.activationId}/api/issue/${issueKey}/articles?operation=jsmIssueViewRelatedArticlesResource`;

		return fetchJson<ArticleResponse>(clientUrl, { method: 'GET' });
	},
});

/**
 * Use this on issue-view routes (browse, embed, queues issue detail, etc.).
 * When {@link fg}('jsm_kb_glance_internal_articles') is on, skips SSR and the initial client
 * resource batch via isBrowserOnly + skipBrowser, while the resource stays registered
 * so {@link useJsmIssueViewRelatedArticlesResource}.refresh() can resolve dependencies.
 */
export const jsmIssueViewRelatedArticlesRouteResource: RouteResource<ArticleResponse | null> =
	Object.defineProperties(safeCloneResource(jsmIssueViewRelatedArticlesResource, {}), {
		skipBrowser: {
			configurable: true,
			enumerable: true,
			get: () => fg('jsm_kb_glance_internal_articles'),
		},
		isBrowserOnly: {
			configurable: true,
			enumerable: true,
			get: () => fg('jsm_kb_glance_internal_articles'),
		},
	});

export const getJsmIssueViewRelatedArticlesRouteResource =
	(): RouteResource<ArticleResponse | null> => jsmIssueViewRelatedArticlesRouteResource;

export const getJsmIssueViewRelatedArticlesResource = (): RouteResource<ArticleResponse | null> =>
	jsmIssueViewRelatedArticlesResource;

export const useJsmIssueViewRelatedArticlesResource =
	(): UseResourceHookResponse<ArticleResponse | null> => {
		return useResource(jsmIssueViewRelatedArticlesResource);
	};

const jsmIssueViewPanelMetadataResource = createResource({
	type: 'JSM_ISSUE_VIEW_PANEL_METADATA_RESOURCE',
	getKey: ({ match, query }: RouterContext) => {
		const issueKey = getIssueKey(match);

		if (!issueKey) {
			if (query && query.selectedIssue) {
				return query.selectedIssue || '';
			}
		}

		return issueKey || '';
	},
	getData: async ({ match, query }: RouterDataContext, { tenantContext }: ResourceStoreContext) => {
		if (tenantContext.appEditions.serviceDesk === 'UNLICENSED') {
			return null;
		}

		let issueKey = getIssueKey(match);

		if (!issueKey) {
			if (query && query.selectedIssue) {
				issueKey = query.selectedIssue;
			}
		}

		if (!issueKey) {
			return null;
		}

		const url = `/rest/servicedesk/1/servicedesk/request/${issueKey}/request-panel-metadata?operation=jsmIssueViewPanelMetadataResource`;

		try {
			return await fetchJson<RequestPanelMetadataResponse>(url, { method: 'GET' });
		} catch (error: unknown) {
			if (
				error instanceof Error &&
				'statusCode' in error &&
				(error.statusCode === 401 || error.statusCode === 403 || error.statusCode === 404)
			) {
				return null;
			}
			throw error;
		}
	},
});

export const getJsmIssueViewPanelMetadataResource =
	(): RouteResource<RequestPanelMetadataResponse | null> => jsmIssueViewPanelMetadataResource;

export const useJsmIssueViewPanelMetadataResource =
	(): UseResourceHookResponse<RequestPanelMetadataResponse | null> => {
		return useResource(jsmIssueViewPanelMetadataResource);
	};

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const getJSDSharedNavigationResources = (): RouteResource<any>[] => {
	return [
		wrapResourceWithServiceDeskLicenseCheck(requiredProjectProperties),
		wrapResourceWithServiceDeskLicenseCheck(requiredFrontendProperties),
		wrapResourceWithServiceDeskLicenseCheck(firstProjectResource),
		wrapResourceWithServiceDeskLicenseCheck(staticProjectSidebarConnectAddonsNavigationResource),
	];
};
